import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CheckoutReviewAndSubmitPageContext } from "@insite/content-library/Pages/CheckoutReviewAndSubmitPage";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import setWorkOrderError from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/WorkOrderErrorHandler";
import setWorkOrder from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/WorkOrderHandler";
import { TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomApplicationState) => {
    const cartState: any = getCurrentCartState(state).value;

    return {
        cartState,
        currentBillTo: getBillToState(state, cartState?.billToId).value,
        workOrderError: state.KGPState.CheckoutReview.workOrderError,
        workOrder: state.KGPState.CheckoutReview.workOrder,
        isAwaitingApproval: cartState?.isAwaitingApproval,
    };
};

const mapDispatchToProps = {
    setWorkOrder,
    setWorkOrderError,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutShippingCarrierServiceStyles {
    customerReferenceGrid?: GridItemProps;
    customerReferenceChildGrid?: GridItemProps;
    customerReferenceSelect?: SelectPresentationProps;
    customerOrderReferenceLabelStyle?: InjectableCss;
}

export const checkoutShippingCarrierServiceStyles: CheckoutShippingCarrierServiceStyles = {
    customerReferenceGrid: {
        width: 12,
        css: css`
            padding: 0px 5px 3px 16px;
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    customerReferenceChildGrid: {
        width: 12,
        css: css`
            padding: 0px 5px 20px 16px;
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    customerOrderReferenceLabelStyle: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smallest};
            font-weight: 600;
        `,
    },
};

/**
 * @deprecated Use checkoutShippingCarrierServiceStyles instead.
 */
export const checkoutShippingCarrierService = checkoutShippingCarrierServiceStyles;
const styles = checkoutShippingCarrierServiceStyles;

const CustomerReference: FC<Props> = ({
    setWorkOrder,
    currentBillTo,
    workOrder,
    workOrderError,
    setWorkOrderError,
    isAwaitingApproval,
}) => {
    const [properties, setProperties] = useState(currentBillTo?.properties);
    const [customerRefChild, setCustomerRefChild] = useState(workOrder || "");
    const [jobId, setJobId] = useState(workOrder || "");
    const [customerRefError, setCustomerRefError] = useState(
        properties?.customerOrderReferenceRequired?.toLowerCase() === "true" ? workOrderError : "",
    );

    // useEffect(() => {
    //     return () => {
    //         setWorkOrder({ workOrder: "" });
    //     };
    // }, []);

    useEffect(() => {
        properties?.customerOrderReferenceRequired?.toLowerCase() === "true" && setCustomerRefError(workOrderError);
    }, [workOrderError]);

    useEffect(() => {
        setProperties(currentBillTo?.properties);
    }, [currentBillTo?.properties]);

    useEffect(() => {
        setWorkOrder({ workOrder: workOrder || "" });
    }, [workOrder]);

    const customerReferenceDropDownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const error =
            e.target.value.trim() === ""
                ? `${siteMessage(properties?.customerOrderReferenceLabel ?? "")} is required`
                : "";

        setCustomerRefChild(e.target.value);
        setWorkOrder({ workOrder: e.target.value });
        if (properties?.customerOrderReferenceRequired?.toLowerCase() === "true") {
            setWorkOrderError({ workOrderError: error });
        }
    };

    const customerReferenceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const error =
            e.target.value.trim() === ""
                ? `${siteMessage(properties?.customerOrderReferenceLabel ?? "")} is required`
                : "";

        setJobId(e.target.value);
        setWorkOrder({ workOrder: e.target.value });
        if (properties?.customerOrderReferenceRequired?.toLowerCase() === "true") {
            setWorkOrderError({ workOrderError: error });
        }
    };

    return (
        <GridContainer>
            <GridItem {...styles.customerReferenceGrid}>
                <StyledWrapper {...styles.customerOrderReferenceLabelStyle}>
                    <Typography>
                        {`${siteMessage(properties?.customerOrderReferenceLabel ?? "")} ${
                            properties?.customerOrderReferenceRequired?.toLowerCase() === "true" ? "*" : ""
                        }`}
                    </Typography>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.customerReferenceChildGrid}>
                {properties?.customerOrderReferenceType === "List" && (
                    <Select
                        {...styles.customerReferenceSelect}
                        label=""
                        value={customerRefChild}
                        onChange={customerReferenceDropDownChange}
                        ref={null}
                        required={properties?.customerOrderReferenceRequired?.toLowerCase() === "true"}
                        error={properties?.customerOrderReferenceRequired?.toLowerCase() === "true" && customerRefError}
                        disabled={isAwaitingApproval}
                    >
                        <option value="">{translate("Select option")}</option>
                        {properties?.listReferencesValues.split("|").map(method => (
                            <option key={method} value={method}>
                                {method}
                            </option>
                        ))}
                    </Select>
                )}
                {(properties?.customerOrderReferenceType === "Text" ||
                    properties?.customerOrderReferenceType === "Default") && (
                    <TextField
                        label={translate("")}
                        value={jobId}
                        onChange={customerReferenceInputChange}
                        required={properties?.customerOrderReferenceRequired?.toLowerCase() === "true"}
                        error={properties?.customerOrderReferenceRequired?.toLowerCase() === "true" && customerRefError}
                        disabled={isAwaitingApproval}
                    />
                )}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomerReference),
    definition: {
        displayName: "Customer Reference",
        group: "Checkout - Review & Submit",
        allowedContexts: [CheckoutReviewAndSubmitPageContext],
    },
};

export default widgetModule;
